import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiOutlineArrowLeft,
  HiOutlineDocumentArrowDown,
  HiOutlineCalendar,
  HiOutlineClipboardDocumentList,
  HiOutlineExclamationTriangle,
  HiOutlineClock,
  HiOutlineCalendarDays,
  HiOutlineCheckCircle,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
  HiOutlineXCircle,
  HiOutlineChartBar,
} from 'react-icons/hi2';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  AreaChart,
  Area,
} from 'recharts';
import pmStandardService from '../services/pmStandardService';
import plantService from '../services/plantService';
import lineService from '../services/lineService';
import machineService from '../services/machineService';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import { SEMANTIC_SUCCESS_MID } from '../theme/chartColors';
import {
  showReportGeneratingToast,
  reportGenerationDone,
  reportGenerationFailed,
} from '../utils/reportGenerationToast';
import './Reports.css';

const FREQUENCY_OPTIONS = ['All', 'Daily', 'Weekly', 'Monthly', 'Quarterly', 'Yearly'];
const STATUS_OPTIONS = ['All', 'Due', 'Completed', 'Overdue'];

// ─── Chart component ──────────────────────────────────────────────────────────
const ChartView = ({ dates, rows }) => {
  if (dates.length === 0) return null;

  // Generate a full timeline between first and last date
  const start = new Date(dates[0]);
  const end = new Date(dates[dates.length - 1]);
  const fullTimeline = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    fullTimeline.push(new Date(d).toISOString().split('T')[0]);
  }

  const chartData = fullTimeline.map((dateStr) => {
    const dateObj = new Date(dateStr);
    const day = dateObj.getDate();
    const month = dateObj.toLocaleDateString(undefined, { month: 'short' });

    const statuses = rows.map(r => r.statusByDate[dateStr]).filter(s => s);
    const completed = statuses.filter(s => s === 'Completed').length;
    const total = statuses.length || 0;
    const rate = total > 0 ? Math.round((completed / total) * 100) : null; // Use null for days with no tasks to avoid 0% drops

    return {
      name: `${day} ${month}`,
      Completed: completed,
      Overdue: statuses.filter(s => s === 'Overdue').length,
      Scheduled: statuses.filter(s => s === 'Scheduled' || s === 'Due').length,
      compliance: rate,
      hasData: total > 0
    };
  });

  return (
    <div style={{ padding: '30px', background: '#fff', borderRadius: '12px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)', margin: '0 24px 24px 24px', border: '1px solid #f1f5f9' }}>
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#1e293b', margin: 0, letterSpacing: '-0.02em' }}>Compliance Performance Trend</h3>
        <p style={{ fontSize: '13px', color: '#64748b', margin: '6px 0 0 0' }}>Daily completion rate across all scheduled maintenance tasks</p>
      </div>
      <div style={{ width: '100%', height: 350 }}>
        <ResponsiveContainer>
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="colorRate" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
            <XAxis dataKey="name" fontSize={10} tickLine={false} axisLine={false} tick={{ fill: '#94a3b8' }} dy={12} interval={Math.ceil(chartData.length / 10)} />
            <YAxis domain={[0, 100]} fontSize={10} tickLine={false} axisLine={false} tick={{ fill: '#94a3b8' }} />
            <Tooltip
              cursor={{ stroke: '#6366f1', strokeWidth: 1, strokeDasharray: '5 5' }}
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  if (!data.hasData) return null;
                  return (
                    <div style={{ backgroundColor: '#fff', padding: '15px', border: '1px solid #e2e8f0; border-color: var(--border-primary)', borderRadius: '12px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)' }}>
                      <p style={{ fontWeight: 800, margin: '0 0 10px 0', borderBottom: '2px solid #f1f5f9', paddingBottom: '6px', fontSize: '14px', color: '#1e293b' }}>{label}</p>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', marginBottom: '10px' }}>
                        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>Compliance Rate:</span>
                        <span style={{ fontSize: '16px', fontWeight: 900, color: data.compliance >= 85 ? SEMANTIC_SUCCESS_MID : '#f59e0b' }}>{data.compliance}%</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', paddingTop: '8px', borderTop: '1px solid #f1f5f9' }}>
                        <p style={{ fontSize: '11px', color: 'var(--accent-primary)', margin: 0, fontWeight: 700 }}>✓ Done: {data.Completed}</p>
                        <p style={{ fontSize: '11px', color: '#dc2626', margin: 0, fontWeight: 700 }}>✗ Late: {data.Overdue}</p>
                        <p style={{ fontSize: '11px', color: '#2563eb', margin: 0, fontWeight: 700 }}>○ Due: {data.Scheduled}</p>
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Area
              type="monotone"
              dataKey="compliance"
              stroke="#6366f1"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorRate)"
              dot={{ r: 4, fill: '#6366f1', strokeWidth: 2, stroke: '#fff' }}
              activeDot={{ r: 6, strokeWidth: 0 }}
              connectNulls={true}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const PmStandardsTracker = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [dateRange, setDateRange] = useState({ 
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], 
    endDate: new Date().toISOString().split('T')[0] 
  });
  const [frequency, setFrequency] = useState('All');
  const [status, setStatus] = useState('All');
  const [plants, setPlants] = useState([]);
  const [allLines, setAllLines] = useState([]);
  const [allMachines, setAllMachines] = useState([]);
  const [selectedPlants, setSelectedPlants] = useState([]);
  const [selectedLines, setSelectedLines] = useState([]);
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [error, setError] = useState('');

  const fetchData = async () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      setError('Please select both Start Date and End Date.');
      return;
    }
    setError('');
    showReportGeneratingToast();
    try {
      setLoading(true);
      const res = await pmStandardService.getIndividualPmTaskReport(
        dateRange.startDate,
        dateRange.endDate,
        frequency === 'All' ? null : frequency,
        status === 'All' ? null : status,
        selectedPlants,
        selectedLines,
        selectedMachines
      );
      setData(res?.tasks || []);
      reportGenerationDone();
    } catch (err) {
      console.error('Error fetching tracker data:', err);
      setData([]);
      reportGenerationFailed('Could not load tracker data.');
    } finally {
      setLoading(false);
    }
  };

  const loadInitialData = async () => {
    try {
      const pData = await plantService.getAllPlants();
      setPlants(pData || []);
      
      const lData = await lineService.getAllLines();
      setAllLines(lData || []);
      
      const mData = await machineService.getAllMachines();
      setAllMachines(mData || []);

      // Pre-select default plant or auto-select if only 1 exists
      // Pre-select plant if only 1 exists
      if (pData?.length === 1) {
        const pId = pData[0].plantId;
        setSelectedPlants([pId]);
      }

      // Auto-select all lines ONLY for single-plant users
      if (pData?.length === 1 && selectedLines.length === 0) {
        const pId = pData[0].plantId;
        const plantLines = (lData || []).filter(l => l.plantId === pId).map(l => l.lineId);
        if (plantLines.length > 0) {
          setSelectedLines(plantLines);
        }
      }
    } catch (err) {
      console.error('Error loading initial filter data:', err);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);



  const lineOptions = selectedPlants.length > 0
    ? allLines.filter(l => selectedPlants.includes(l.plantId))
    : allLines;

  const machineOptions = selectedLines.length > 0
    ? allMachines.filter(m => selectedLines.includes(m.lineId))
    : selectedPlants.length > 0
      ? allMachines.filter(m => lineOptions.some(l => l.lineId === m.lineId))
      : allMachines;

  const handlePlantChange = (vals) => {
    setSelectedPlants(vals);
    if (vals.length > 0) {
        localStorage.setItem('defaultPlantId', vals[0].toString());
    }
    const validLines = allLines.filter(l => vals.includes(l.plantId)).map(l => l.lineId);
    setSelectedLines(prev => prev.filter(id => validLines.includes(id)));
    
    const validMachines = allMachines.filter(m => validLines.includes(m.lineId)).map(m => m.machineId);
    setSelectedMachines(prev => prev.filter(id => validMachines.includes(id)));
  };

  const handleLineChange = (vals) => {
    setSelectedLines(vals);
    const validMachines = allMachines.filter(m => vals.includes(m.lineId)).map(m => m.machineId);
    setSelectedMachines(prev => prev.filter(id => validMachines.includes(id)));
  };

  const handleMachineChange = (vals) => {
    setSelectedMachines(vals);
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({ ...prev, [name]: value }));
  };

  // --- Matrix Data Prep ---
  const uniqueDates = [...new Set(data.filter(d => d.dueDate).map(d => new Date(d.dueDate).toISOString().split('T')[0]))].sort();

  const groupedData = data.reduce((acc, curr) => {
    const key = `${curr.standardCode}_${curr.machineName}`;
    if (!acc[key]) {
      acc[key] = {
        standardCode: curr.standardCode,
        standardName: curr.standardName,
        machineName: curr.machineName,
        frequency: curr.frequency,
        doneBy: curr.doneBy,
        why: curr.why,
        how: curr.how,
        shutdown: curr.isMachineShutdownRequired,
        time: curr.minutesSpent,
        statusByDate: {}
      };
    }
    const dateStr = curr.dueDate ? new Date(curr.dueDate).toISOString().split('T')[0] : '';
    if (dateStr) {
      acc[key].statusByDate[dateStr] = curr.status;
    }
    return acc;
  }, {});

  const matrixRows = Object.values(groupedData);

  const getStatusIcon = (status) => {
    if (status === 'Completed') return <HiOutlineCheckCircle style={{ color: SEMANTIC_SUCCESS_MID, margin: '0 auto', display: 'block' }} size={22} title="Completed" />;
    if (status === 'Overdue') return <HiOutlineXCircle style={{ color: '#ef4444', margin: '0 auto', display: 'block' }} size={21} title="Overdue" />;
    if (status === 'Scheduled' || status === 'Due') return <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#3b82f6', margin: '0 auto' }} title={status} />;
    if (status === 'Skipped') return <span style={{ color: '#94a3b8', fontSize: '10px', textAlign: 'center', display: 'block', fontWeight: 'bold' }}>SKIP</span>;
    return <span style={{ color: '#cbd5e1', display: 'block', textAlign: 'center' }}>-</span>;
  };

  const exportToExcel = () => {
    if (matrixRows.length === 0) return;
    const worksheet = XLSX.utils.json_to_sheet(
      matrixRows.map((item) => {
        const rowData = {
          WHAT: `${item.standardCode} - ${item.standardName}`,
          WHERE: item.machineName,
          WHEN: item.frequency,
          WHO: item.doneBy || '-',
          WHY: item.why,
          HOW: item.how,
          SHUTDOWN: item.shutdown ? 'YES' : 'No',
          TIME: item.time ? `${item.time}m` : '-',
        };
        uniqueDates.forEach((date) => {
          const displayDate = new Date(date).toLocaleDateString();
          rowData[displayDate] = item.statusByDate[date] || '-';
        });
        return rowData;
      })
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'PM Standards Tracker');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const dataBlob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
    saveAs(dataBlob, `PM_Standards_Tracker_${dateRange.startDate}_to_${dateRange.endDate}.xlsx`);
  };

  const [viewType, setViewType] = useState('table');

  const WHAT_COL_WIDTH = 220;
  const WHERE_COL_WIDTH = 180;

  const LegendBar = () => (
    <div style={{ display: 'flex', alignItems: 'center', borderRadius: "10px", justifyContent: 'space-between', padding: '10px 10px', backgroundColor: '#fff', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap', gap: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', backgroundColor: '#fee2e2', color: '#991b1b', padding: '3px 12px', borderRadius: '16px', fontSize: '11px', fontWeight: 700, border: '1px solid #ef4444' }}>
          <HiOutlineXCircle size={14} /> Overdue
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', backgroundColor: '#d1fae5', color: '#065f46', padding: '3px 12px', borderRadius: '16px', fontSize: '11px', fontWeight: 700, border: `1px solid ${SEMANTIC_SUCCESS_MID}` }}>
          <HiOutlineCheckCircle size={14} /> Completed
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', backgroundColor: '#fef3c7', color: '#92400e', padding: '3px 12px', borderRadius: '16px', fontSize: '11px', fontWeight: 700, border: '1px solid #f59e0b' }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#3b82f6' }} /> Due
        </span>
        <span style={{ backgroundColor: '#f1f5f9', color: '#475569', padding: '3px 12px', borderRadius: '16px', fontSize: '11px', fontWeight: 700, border: '1px solid #cbd5e1' }}>SKIP</span>
      </div>
    </div>
  );

  return (
    <div className="pagecontainer report-page">
      <div className="page-header page-header--report-tight">
        <div className="oee-report-header-left">
          <button className="oee-report-back-btn" onClick={() => navigate('/home/reports')}>
            <HiOutlineArrowLeft size={18} />
          </button>
          <div>
            <h1 className="oee-report-title">
              <HiOutlineChartBar /> PM Standards Tracker
            </h1>
            <p className="oee-report-subtitle">
              Visual completion matrix per machine per day
            </p>
          </div>
        </div>
        <div className="header-actions">
          <button className="oee-filter-toggle-btn" onClick={() => setShowFilters((p) => !p)}>
            <HiOutlineCalendar size={16} /> Filters {showFilters ? <HiOutlineChevronUp size={13} /> : <HiOutlineChevronDown size={13} />}
          </button>
          <button type="button" className="btn-primary" onClick={exportToExcel} disabled={loading || matrixRows.length === 0}>
            <HiOutlineDocumentArrowDown /> Export
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="reportbox searchfilterbox">
          <div className="report-filters">
            <div className="filter-group">
              <label>Start Date</label>
              <input type="date" name="startDate" value={dateRange.startDate} onChange={handleDateChange} className={`filter-input ${error && !dateRange.startDate ? 'input-error' : ''}`} />
            </div>
            <div className="filter-group">
              <label>End Date</label>
              <input type="date" name="endDate" value={dateRange.endDate} onChange={handleDateChange} className={`filter-input ${error && !dateRange.endDate ? 'input-error' : ''}`} />
            </div>
            <div className="filter-group">
              <label>Frequency</label>
              <select value={frequency} onChange={(e) => setFrequency(e.target.value)} className="filter-input" style={{ appearance: 'auto' }}>
                {FREQUENCY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="filter-group">
              <label>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="filter-input" style={{ appearance: 'auto' }}>
                {STATUS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="filter-group">
              <label>Plant</label>
              <MultiSelectDropdown
                options={plants}
                selectedValues={selectedPlants}
                onChange={handlePlantChange}
                placeholder="All Plants"
                displayKey="plantName"
                valueKey="plantId"
              />
            </div>
            <div className="filter-group">
              <label>LINE</label>
              <MultiSelectDropdown
                options={lineOptions}
                selectedValues={selectedLines}
                onChange={handleLineChange}
                placeholder="All Lines"
                displayKey="lineName"
                valueKey="lineId"
                disabled={selectedPlants.length === 0}
              />
            </div>
            <div className="filter-group">
              <label>Machines</label>
              <MultiSelectDropdown
                options={machineOptions}
                selectedValues={selectedMachines}
                onChange={handleMachineChange}
                placeholder="All Machines"
                displayKey="machineName"
                valueKey="machineId"
                disabled={selectedLines.length === 0 && selectedPlants.length === 0}
              />
            </div>
          </div>

          <div className="report-filter-actions">
            {(selectedPlants.length > 0 || selectedLines.length > 0 || selectedMachines.length > 0) && (
              <button 
                className="oee-clear-filters-btn" 
                onClick={() => {
                  setSelectedPlants([]);
                  setSelectedLines([]);
                  setSelectedMachines([]);
                  setData([]);
                }}
              >
                ✕ Clear Filters
              </button>
            )}
            <button type="button" className="btn-primary" onClick={fetchData} disabled={loading}>
              <HiOutlineCalendar /> Generate Report
            </button>
          </div>
          {error && <p style={{ color: '#ef4444', fontSize: '13px', marginTop: '12px', fontWeight: 600, textAlign: 'center' }}>{error}</p>}
        </div>
      )}

      {matrixRows.length > 0 && !loading && (
        <>
          <div className="viewTypeheader" style={{ borderBottom: '1px solid #e5e7eb', padding: '10px 24px', backgroundColor: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>View Type:</label>
              <button className={`btn ${viewType === 'table' ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: '12px', padding: '4px 12px' }} onClick={() => setViewType('table')}>Table View</button>
              <button className={`btn ${viewType === 'chart' ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: '12px', padding: '4px 12px' }} onClick={() => setViewType('chart')}>Chart View</button>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[
                { label: 'MACHINES', value: [...new Set(matrixRows.map(r => r.machineName))].length, color: '#6366f1' },
                { label: 'DAYS', value: uniqueDates.length, color: '#0ea5e9' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 20, backgroundColor: color + '15', border: `1px solid ${color}30` }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
                  <span style={{ fontSize: 13, fontWeight: 800, color }}>{value}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginTop: "10px", marginBottom: "10px" }}>
            <LegendBar />
          </div>

          {viewType === 'table' ? (
            <div className="lrt-wrapper" style={{ margin: '0 24px 24px 0px', overflowX: 'auto', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <table className="lrt-table" style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
                <thead>
                  {/* Formulas / Legend row */}
                  <tr style={{ background: '#f8fafc' }}>
                    <td colSpan={2} style={{ padding: '10px 15px', fontSize: '11px', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #e2e8f0; border-color: var(--border-primary)' }}>
                      <strong>✅</strong> Complied &nbsp; <strong>❌</strong> Overdue &nbsp; <strong>🔵</strong> Scheduled
                    </td>
                    {uniqueDates.map((date, i) => (
                      <td key={i} style={{ textAlign: 'center', fontSize: '11px', color: '#1e293b', fontWeight: 700, borderBottom: '1px solid #e2e8f0', background: '#f1f5f9' }}>
                        {new Date(date).getDate()}
                      </td>
                    ))}
                    <td style={{ textAlign: 'center', fontSize: '11px', color: '#1e293b', fontWeight: 700, borderBottom: '1px solid #e2e8f0', background: '#f1f5f9' }}>Avg</td>
                  </tr>
                  {/* Dark Column headers */}
                  <tr style={{ background: '#1e293b' }}>
                    <th style={{ color: '#fff', textAlign: 'left', padding: '12px 15px', fontSize: '12px', fontWeight: 700, borderRight: '1px solid #334155', minWidth: WHAT_COL_WIDTH, width: WHAT_COL_WIDTH }}>Standard / WHAT</th>
                    <th style={{ color: '#fff', textAlign: 'left', padding: '12px 15px', fontSize: '12px', fontWeight: 700, borderRight: '1px solid #334155', minWidth: WHERE_COL_WIDTH, width: WHERE_COL_WIDTH }}>Machine / WHERE</th>
                    {uniqueDates.map((_, i) => (
                      <th key={i} style={{ color: '#fff', textAlign: 'center', fontSize: '12px', fontWeight: 700, minWidth: '40px', borderRight: '1px solid #334155' }}>{i + 1}</th>
                    ))}
                    <th style={{ color: '#fff', textAlign: 'center', fontSize: '12px', fontWeight: 700, minWidth: '60px' }}>Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {matrixRows.map((row, rIdx) => {
                    const items = Object.values(row.statusByDate);
                    const compl = items.filter(s => s === 'Completed').length;
                    const rate = items.length > 0 ? Math.round((compl / items.length) * 100) : 0;

                    return (
                      <tr key={rIdx} style={{ background: rIdx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                        <td style={{ borderRight: '1px solid #e2e8f0', padding: '10px 15px', minWidth: WHAT_COL_WIDTH, width: WHAT_COL_WIDTH }}>
                          <div style={{ color: '#0ea5e9', fontWeight: 700, fontSize: '13px' }}>{row.standardCode}</div>
                          <div style={{ fontSize: '11px', color: '#64748b' }}>{row.standardName}</div>
                        </td>
                        <td style={{ borderRight: '1px solid #e2e8f0', padding: '10px 15px', minWidth: WHERE_COL_WIDTH, width: WHERE_COL_WIDTH }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: '#334155' }}>{row.machineName}</div>
                        </td>
                        {uniqueDates.map((date, i) => (
                          <td key={i} style={{ textAlign: 'center', borderRight: '1px solid #f1f5f9', padding: '5px' }}>
                            {getStatusIcon(row.statusByDate[date])}
                          </td>
                        ))}
                        <td style={{ textAlign: 'center', fontWeight: 700, color: rate >= 85 ? SEMANTIC_SUCCESS_MID : rate >= 65 ? '#f59e0b' : '#ef4444', backgroundColor: rate >= 85 ? '#f0fdf4' : rate >= 65 ? '#fffbeb' : '#fef2f2' }}>
                          {rate}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <ChartView dates={uniqueDates} rows={matrixRows} />
          )}
        </>
      )}

      {!matrixRows.length && !loading && (
        <div style={{ textAlign: 'center' }}>
          <div className="report-placeholder" style={{ padding: '60px', border: '2px dashed #e2e8f0; border-color: var(--border-primary)', borderRadius: '16px', background: '#fff' }}>
            <HiOutlineChartBar style={{ fontSize: '64px', color: '#cbd5e1', marginBottom: '20px' }} />
            <h3>PM Standards Tracker</h3>
            <p style={{ color: '#94a3b8' }}>Select a date range and click "Generate Report" to build the matrix</p>
          </div>
        </div>
      )}

      {loading && (
        <div style={{ padding: '100px 24px', textAlign: 'center' }}>
          <span className="spinner" style={{ width: '48px', height: '48px' }} />
          <p style={{ marginTop: '20px', color: '#64748b', fontWeight: 600 }}>Analyzing maintenance standards...</p>
        </div>
      )}
    </div>
  );
};

export default PmStandardsTracker;
